/* eslint-disable @typescript-eslint/no-explicit-any  */
/* eslint-disable react-hooks/rules-of-hooks  */
import type { UseQueryOptions, UseMutationOptions, UseQueryResult, UseMutationResult } from '@tanstack/react-query'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { apis } from '@core/services/apis'
import { fillUrl } from '@core/utils/fill-url'
import type { MethodType, CustomAxiosRequestConfig } from '@core/utils/client'
import { httpClient } from '@core/utils/client'

const cfgToClientMethodMapper: Record<string, MethodType> = {
  GET: 'get',
  POST: 'post',
  PUT: 'put',
  DELETE: 'delete'
}

type Services = typeof apis
type Scope = keyof Services
type Endpoint<S extends Scope> = keyof Services[S]
type ServiceDef<S extends Scope, M extends Endpoint<S>> = Services[S][M] & {
  url: string
  method: string
}

// Helpers to extract pieces out of ServiceDef
type ApiPathParams<S extends Scope, M extends Endpoint<S>> =
  ServiceDef<S, M> extends { pathParams: infer P } ? P : undefined
type ApiParams<S extends Scope, M extends Endpoint<S>> = ServiceDef<S, M> extends { params: infer P } ? P : undefined

type ApiRequestBody<S extends Scope, M extends Endpoint<S>> =
  ServiceDef<S, M> extends { request: infer R } ? R : undefined

type ApiResponseBody<S extends Scope, M extends Endpoint<S>> =
  ServiceDef<S, M> extends { response: infer R } ? R : unknown

type TPathParams<S extends Scope, M extends Endpoint<S>> = {
  pathParams?: ApiPathParams<S, M>
}
type TParams<S extends Scope, M extends Endpoint<S>> = {
  params?: ApiParams<S, M>
}

// Type guard to determine if an API endpoint is a GET method
type IsGetEndpoint<S extends Scope, M extends Endpoint<S>> = ServiceDef<S, M> extends { method: 'GET' } ? true : false

// Helper type to get the proper return type based on HTTP method
type ApiReturnType<S extends Scope, M extends Endpoint<S>> =
  IsGetEndpoint<S, M> extends true
    ? UseQueryResult<ApiResponseBody<S, M>, Error>
    : UseMutationResult<ApiResponseBody<S, M>, Error, ApiRequestBody<S, M>>

type ApiOptionsType<S extends Scope, M extends Endpoint<S>> =
  IsGetEndpoint<S, M> extends true
    ? Omit<UseQueryOptions<ApiResponseBody<S, M>, Error, ApiResponseBody<S, M>>, 'queryKey' | 'queryFn'> &
        TPathParams<S, M> &
        TParams<S, M> &
        Partial<{ dependency: Array<any>; skipGlobalErrorHandler?: boolean; timeout?: number }>
    : UseMutationOptions<ApiResponseBody<S, M>, Error, ApiRequestBody<S, M>> &
        TPathParams<S, M> &
        TParams<S, M> &
        Partial<{ skipGlobalErrorHandler?: boolean; timeout?: number }>

// For GET methods
export function useAPI<S extends Scope, M extends Endpoint<S>>(
  key: [S, M],
  options?: ApiOptionsType<S, M>
): ApiReturnType<S, M>

// Implementation - make it compatible with all overloads
export function useAPI<S extends Scope, M extends Endpoint<S>>(key: [S, M], options?: any): any {
  const [scope, endpoint] = key

  if (!scope || !endpoint) {
    console.warn('Invalid API key provided to useAPI:', key)

    // Return a stub object with all possible properties to prevent runtime errors
    return {
      data: undefined,
      error: null,
      isLoading: false,
      isSuccess: false,
      isError: false,
      mutate: () => Promise.resolve(),
      mutateAsync: () => Promise.resolve()
    }
  }

  const cfg: any = apis[scope][endpoint]
  const queryKey = [scope, endpoint] as const

  if (cfg?.method === 'GET') {
    return useQuery({
      queryKey: [...queryKey, options?.dependency],
      queryFn: () =>
        httpClient.get(fillUrl(cfg.url, options?.pathParams), {
          params: options?.params,
          skipGlobalErrorHandler: options?.skipGlobalErrorHandler,
          timeout: options?.timeout
        }),
      ...options
    })
  }

  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: queryKey,
    mutationFn: (body: any) => {
      const method = cfgToClientMethodMapper[cfg?.method || 'POST']
      const url = fillUrl(cfg?.url || '', options?.pathParams)

      const config: CustomAxiosRequestConfig = {
        skipGlobalErrorHandler: options?.skipGlobalErrorHandler,
        timeout: options?.timeout
      }

      if (method === 'delete') {
        return httpClient[method](url, config)
      }

      return httpClient[method as 'post' | 'put'](url, body, config)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
    },

    ...options
  })
}
